
import React, { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Hook for lazy loading data when a component becomes visible
 * @param threshold The intersection threshold (0-1)
 * @param rootMargin Margin around the root element
 */
export function useIntersectionObserver(
  threshold = 0.1,
  rootMargin = '0px'
): [React.RefObject<HTMLElement>, boolean] {
  const [isVisible, setIsVisible] = useState(false);
  const ref = React.useRef<HTMLElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const callback = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    setIsVisible(entry.isIntersecting);

    if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
      if (ref.current && threshold === 1 && observerRef.current) {
        observerRef.current.unobserve(ref.current);
      }
    }
  }, [threshold]);

  useEffect(() => {
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(callback, { threshold, rootMargin });
    }
    const currentRef = ref.current;
    const observer = observerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }
    return () => {
      if (currentRef && observer) {
        observer.unobserve(currentRef);
      }
    };
  }, [callback, rootMargin, threshold]);

  return [ref, isVisible];
}
